import { z } from 'zod';
import { localeSchema, uuid } from './common.js';
import { themePref, userPublicSchema } from './user.js';
/**
 * Settings op DTOs (F10/E18). Each is one shared schema, client + server.
 */
// --- F10 profile (`settings.getProfile` / `settings.updateProfile`) ---------
//
// "settings.locale.set" in the M4 op inventory maps to the ALREADY-SHIPPED
// `settings.setLocale` op (`setLocaleInput` below, M0/F0j) — extended here,
// not duplicated under a new name.
/** How the account authenticates (F10/F12) — surfaced read-only next to the profile form. */
export const authMethod = z.enum(['password', 'google', 'magic_link']);
/**
 * `settings.getProfile` result — the safe user projection plus the derived
 * `authMethods` list (joined from `oauthIdentities` + `passwordHash IS NOT
 * NULL` server-side; never re-exposes the hash itself).
 */
export const settingsProfileResult = userPublicSchema.extend({
    authMethods: z.array(authMethod),
});
/**
 * `settings.updateProfile` — display name + avatar only (email/locale/theme
 * have their own dedicated ops/pages, F10/F12/E18). `avatarFileId: null`
 * resets to the default mascot avatar (F10 AC).
 */
export const settingsProfileUpdateInput = z.object({
    displayName: z
        .string()
        .trim()
        .min(1, 'errors:settings.profile.displayName.required')
        .max(120, 'errors:settings.profile.displayName.tooLong')
        .optional(),
    avatarFileId: uuid.nullable().optional(),
});
export const settingsProfileUpdateResult = settingsProfileResult;
/** `settings.setLocale` — persists `users.locale`, pushes `user:{id}` (§20). */
export const setLocaleInput = z.object({ locale: localeSchema });
/** `settings.setTheme` — persists `users.theme`. */
export const setThemeInput = z.object({ theme: themePref });
/** One per-type notification preference (per-channel toggles, §7). */
export const notificationPref = z.object({
    type: z.string().min(1),
    inApp: z.boolean(),
    webPush: z.boolean(),
    email: z.boolean(),
});
export const updateNotificationPrefsInput = z.object({
    prefs: z.array(notificationPref).max(100),
});
